import { z } from 'zod';

/**
 * Standard Israeli identifier checksum (9 digits, alternating *1 / *2,
 * digit-sum of products mod 10 === 0). Mirrors the backend validator.
 */
export const isValidIsraeliId = (value: string): boolean => {
  if (!/^\d{9}$/.test(value)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let n = Number(value[i]) * ((i % 2) + 1);
    if (n > 9) n -= 9;
    sum += n;
  }
  return sum % 10 === 0;
};

export const isValidIsraeliMobile = (value: string): boolean =>
  /^05\d{8}$/.test(value);

export const emailSchema = z.string().trim().min(1).email();

export const isValidEmail = (value: string): boolean =>
  emailSchema.safeParse(value).success;

export const normalizeEmail = (value: string): string =>
  emailSchema.parse(value);

export const validateBankAccountInfo = (b: {
  bankName: string;
  bankBranchNumber: string;
  bankAccountNumber: string;
  bankAccountOwnerName: string;
  bankAccountOwnerId: string;
}): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!b.bankName.trim()) errors.bankName = 'onboarding.errors.bankNameRequired';
  if (!/^\d+$/.test(b.bankBranchNumber))
    errors.bankBranchNumber = 'onboarding.errors.bankBranchNumberInvalid';
  if (!/^\d+$/.test(b.bankAccountNumber))
    errors.bankAccountNumber = 'onboarding.errors.bankAccountNumberInvalid';
  if (!b.bankAccountOwnerName.trim())
    errors.bankAccountOwnerName = 'onboarding.errors.bankAccountOwnerNameRequired';
  if (!isValidIsraeliId(b.bankAccountOwnerId))
    errors.bankAccountOwnerId = 'onboarding.errors.bankAccountOwnerIdInvalid';
  return errors;
};

export const validatePersonalInfo = (p: {
  fullName: string;
  phoneNumber: string;
  email: string;
  taxId: string;
  dateOfBirth: string;
  city: string;
  spokenLanguages: string[];
}): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!p.fullName.trim()) errors.fullName = 'onboarding.errors.fullNameRequired';
  if (!isValidIsraeliMobile(p.phoneNumber))
    errors.phoneNumber = 'onboarding.errors.phoneInvalid';
  if (!isValidEmail(p.email)) errors.email = 'onboarding.errors.emailInvalid';
  if (!isValidIsraeliId(p.taxId)) errors.taxId = 'onboarding.errors.taxIdInvalid';
  if (!p.dateOfBirth)
    errors.dateOfBirth = 'onboarding.errors.dateOfBirthRequired';
  if (!p.city) errors.city = 'onboarding.errors.cityRequired';
  if (!p.spokenLanguages || p.spokenLanguages.length === 0)
    errors.spokenLanguages = 'onboarding.errors.spokenLanguagesRequired';
  return errors;
};

export const validateLeadBasicInfo = (p: {
  fullName: string;
  phoneNumber: string;
  email: string;
}): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!p.fullName.trim()) errors.fullName = 'onboarding.errors.fullNameRequired';
  if (!isValidIsraeliMobile(p.phoneNumber))
    errors.phoneNumber = 'onboarding.errors.phoneInvalid';
  if (!isValidEmail(p.email)) errors.email = 'onboarding.errors.emailInvalid';
  return errors;
};

export const validatePersonalIdentityInfo = (p: {
  taxId: string;
  dateOfBirth: string;
}): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!isValidIsraeliId(p.taxId)) errors.taxId = 'onboarding.errors.taxIdInvalid';
  if (!p.dateOfBirth)
    errors.dateOfBirth = 'onboarding.errors.dateOfBirthRequired';
  return errors;
};

export const validatePersonalDetails = (p: {
  city: string;
  spokenLanguages: string[];
}): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!p.city) errors.city = 'onboarding.errors.cityRequired';
  if (!p.spokenLanguages || p.spokenLanguages.length === 0)
    errors.spokenLanguages = 'onboarding.errors.spokenLanguagesRequired';
  return errors;
};
